import { normalizeSummaryMarkdown } from './normalizeSummaryMarkdown';
// Markdown → HTML renderer.
// Producteert HTML die past binnen `.notion-editor` styling (zie SummaryEditor.css).
// Dezelfde basis als de parser in SummaryEditor zodat het verslag er identiek uitziet.

// Een markdown-regel die een lijstitem opent (zowel - / * als genummerd),
// met optionele leading whitespace en een mogelijke escape (\-).
const LIST_ITEM_RE = /^\s*(\\?[-*]|\d+\.)\s+\S/;

// Verwijder blanco regels die tussen twee lijstitems staan, zodat losse
// "loose" lijsten (item\n\nitem) als één strakke lijst worden weergegeven.
// Blanco regels tussen een lijst en gewone tekst/kop blijven behouden.
export const collapseListItemBlankLines = (content: string): string => {
    if (!content) return content;

    const lines = content.split('\n');
    const result: string[] = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        if (line.trim() === '') {
            // Vorige niet-lege regel (uit de al verwerkte output)
            let prev = '';
            for (let k = result.length - 1; k >= 0; k--) {
                if (result[k].trim() !== '') { prev = result[k]; break; }
            }
            // Volgende niet-lege regel
            let next = '';
            for (let k = i + 1; k < lines.length; k++) {
                if (lines[k].trim() !== '') { next = lines[k]; break; }
            }
            // Blanco regel tussen twee lijstitems → overslaan
            if (LIST_ITEM_RE.test(prev) && LIST_ITEM_RE.test(next)) {
                continue;
            }
        }

        result.push(line);
    }

    return result.join('\n');
};

const escapeHtml = (raw: string): string =>
    raw
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

const processInlineMarkdown = (text: string): string => {
    const unescaped = text
        .replace(/\\(\*)/g, '$1')
        .replace(/\\(_)/g, '$1')
        .replace(/\\(`)/g, '$1')
        .replace(/\\(\|)/g, '$1')
        .replace(/\\(-)/g, '$1')
        .replace(/\\\./g, '.')
        .replace(/\\\[/g, '[')
        .replace(/\\\]/g, ']')
        .replace(/\\\\/g, '\\');

    return unescaped
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/__([^_]+?)__/g, '<strong>$1</strong>')
        .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
        .replace(/_([^_]+?)_/g, '<em>$1</em>')
        .replace(/`([^`]+?)`/g, '<code>$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
};

export const markdownToHtml = (markdown: string): string => {
    if (!markdown) return '';

    // Strip escaped blockquote markers (\> → >) at the start of lines
    const preprocessed = normalizeSummaryMarkdown(markdown).replace(/^\\>/gm, '>');

    const lines = escapeHtml(collapseListItemBlankLines(preprocessed)).split('\n');
    let html = '';
    let inList = false;
    let listType: 'ul' | 'ol' | null = null;
    let inTable = false;
    let tableRows: string[] = [];

    const closeList = () => {
        if (inList && listType) {
            html += `</${listType}>\n`;
            inList = false;
            listType = null;
        }
    };

    const closeTable = () => {
        if (inTable && tableRows.length > 0) {
            html += '<table>\n';
            const headerCells = tableRows[0].split('|').map(cell => cell.trim()).filter(cell => cell !== '');
            html += '<thead>\n<tr>\n';
            headerCells.forEach((cell) => {
                html += `<th>${processInlineMarkdown(cell)}</th>\n`;
            });
            html += '</tr>\n</thead>\n';

            if (tableRows.length > 1) {
                html += '<tbody>\n';
                const startIndex = tableRows[1].includes('---') ? 2 : 1;
                for (let i = startIndex; i < tableRows.length; i++) {
                    const cells = tableRows[i].split('|').map(cell => cell.trim()).filter(cell => cell !== '');
                    if (cells.length > 0) {
                        html += '<tr>\n';
                        html += cells.map(cell => `<td>${processInlineMarkdown(cell)}</td>\n`).join('');
                        html += '</tr>\n';
                    }
                }
                html += '</tbody>\n';
            }
            html += '</table>\n';
        }
        tableRows = [];
        inTable = false;
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmedLine = line.trim();

        const isTableRow = trimmedLine.includes('|') && trimmedLine.split('|').length > 2;
        const isTableSeparator = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(trimmedLine);

        if (isTableRow || isTableSeparator) {
            if (!inTable) {
                closeList();
                inTable = true;
            }
            if (!isTableSeparator) {
                tableRows.push(trimmedLine);
            }
            continue;
        } else if (inTable) {
            closeTable();
        }

        if (trimmedLine.startsWith('#')) {
            closeList();
            const level = trimmedLine.match(/^#+/)?.[0].length || 0;
            if (level > 0 && level <= 6) {
                const content = trimmedLine.substring(level).trim();
                const cappedLevel = Math.min(level, 4);
                html += `<h${cappedLevel}>${processInlineMarkdown(content)}</h${cappedLevel}>\n`;
            }
            continue;
        }

        // Blockquote — controleer op &gt; omdat escapeHtml al gelopen heeft
        if (trimmedLine.startsWith('&gt;')) {
            closeList();
            const content = trimmedLine.replace(/^&gt;\s?/, '');
            html += `<blockquote><p>${processInlineMarkdown(content)}</p></blockquote>\n`;
            continue;
        }

        const ulMatch = line.match(/^\s*\\?[-*]\s+(.*)/);
        if (ulMatch) {
            const content = ulMatch[1];
            if (content.trim() === '') continue;
            if (!inList || listType !== 'ul') {
                closeList();
                html += '<ul>\n';
                inList = true;
                listType = 'ul';
            }
            html += `<li>${processInlineMarkdown(content)}</li>\n`;
            continue;
        }

        const olMatch = line.match(/^\s*\d+\.\s+(.*)/);
        if (olMatch) {
            const content = olMatch[1];
            if (content.trim() === '') continue;
            if (!inList || listType !== 'ol') {
                closeList();
                html += '<ol>\n';
                inList = true;
                listType = 'ol';
            }
            html += `<li>${processInlineMarkdown(content)}</li>\n`;
            continue;
        }

        // Lege regels binnen een lijst sluiten de lijst niet af, zodat
        // items met dubbele newlines ertussen aaneengesloten renderen
        if (!trimmedLine && inList) {
            continue;
        }

        closeList();

        if (trimmedLine) {
            html += `<p>${processInlineMarkdown(trimmedLine)}</p>\n`;
        }
    }

    closeList();
    closeTable();

    return html.trim();
};
